import { useState } from 'react'
import { Check, ChevronsUpDown, X } from 'lucide-react'
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { useContrastedBackground } from 'app/frontend/base/components/Card'
import { cn } from '@/lib/utils'
import {
  useActiveTeams,
  useSetCandidateTeams,
  useUpdateCandidate,
} from '../hooks'

type Props = {
  candidate: wowaudit.GuildCandidate
  size?: 'xs' | 'default'
  className?: string
}

const CandidateTeamSelect = ({
  candidate,
  size = 'default',
  className,
}: Props) => {
  const background = useContrastedBackground()
  const [open, setOpen] = useState(false)
  const { mutate: setTeams, isPending: settingTeams } = useSetCandidateTeams()
  const { mutate: update, isPending: updating } = useUpdateCandidate()
  const teams = useActiveTeams()
  const characterId = candidate.characterReference.id
  const selectedTeams = teams.filter((team) =>
    candidate.teamIds.includes(team.id),
  )
  const rejected = candidate.status === 'rejected'

  const toggle = (teamId: number) => {
    const teamIds = candidate.teamIds.includes(teamId)
      ? candidate.teamIds.filter((id) => id !== teamId)
      : [...candidate.teamIds, teamId]

    setTeams({ characterId, teamIds })
    setOpen(false)
  }

  const toggleRejected = () => {
    update({ characterId, status: rejected ? 'approved' : 'rejected' })
    setOpen(false)
  }

  const iconClassName = size === 'xs' ? 'h-3 w-3' : 'h-4 w-4'

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type='button'
          disabled={settingTeams || updating}
          className={cn(
            'inline-flex items-center justify-between gap-1 border rounded-md whitespace-nowrap disabled:opacity-50',
            background,
            size === 'xs' ? 'h-5 px-1.5 text-xs w-20' : 'h-8 px-2 text-sm w-28',
            className,
          )}
        >
          <span
            className={cn(
              'truncate',
              rejected && 'text-destructive-foreground',
              !rejected &&
                selectedTeams.length === 0 &&
                'text-muted-foreground',
              selectedTeams.length > 1 && 'italic',
            )}
          >
            {rejected
              ? 'Rejected'
              : selectedTeams.length === 0
              ? 'No team'
              : selectedTeams.length === 1
              ? selectedTeams[0].name
              : 'Multiple'}
          </span>
          {selectedTeams.length > 0 ? (
            <X
              className={cn(
                'shrink-0 text-muted-foreground hover:text-foreground transition-colors',
                iconClassName,
              )}
              onClick={(e) => {
                e.preventDefault()
                setTeams({ characterId, teamIds: [] })
              }}
            />
          ) : (
            <ChevronsUpDown
              className={cn('shrink-0 text-muted-foreground', iconClassName)}
            />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align='end' className='p-0 w-56'>
        <Command>
          <CommandList className='h-auto'>
            {teams.length > 0 && (
              <>
                <CommandGroup>
                  {teams.map((team) => (
                    <CommandItem
                      key={team.id}
                      value={`${team.id}-${team.name}`}
                      onSelect={() => toggle(team.id)}
                      className='cursor-pointer'
                    >
                      <div className='flex w-full items-center justify-between gap-2'>
                        <span className='truncate'>Team {team.name}</span>
                        {candidate.teamIds.includes(team.id) ? (
                          <Check className='h-4 w-4' />
                        ) : null}
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
                <CommandSeparator />
              </>
            )}
            <CommandGroup>
              <CommandItem
                value='reject'
                onSelect={toggleRejected}
                className='cursor-pointer'
              >
                <div className='flex w-full items-center justify-between gap-2'>
                  <span className='text-destructive-foreground'>Reject</span>
                  {rejected ? <Check className='h-4 w-4' /> : null}
                </div>
              </CommandItem>
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

export default CandidateTeamSelect
